// See hooks.ts. Owned by the country family of kits (docs/186 EA7): the outdoor countries the forge places
// (crates/fk-schema season.rs `grounds()`, genesis.rs Ground → kit). What the shared painter doesn't already
// draw for them, each from the kit's hand-laid board script and notes (docs/166 C2):
//  - mud for part of a mire's or jungle's rough (`mire_board.py.txt`: 2×2 seeded blocks), puddles on it,
//    the mire's lily pads at its own rate;
//  - named roughs (`rough_brambles`, `rough_sand_drift`) in seeded patches among the rough;
//  - ice: frozen water's seeded sheets and a square with the `ice` flag (docs/55), each frayed over the
//    open water by rank, with a dark wet line and no rim against the land (`snow_board.py.txt`);
//  - lava for the heart of an Ashlands lake, water fraying over it (`ashlands_board.py.txt`);
//  - a big standing piece (`big_oak`, `big_spruce`, `big_palms`, `big_tree`) over a 2×2 block of wall squares;
//  - decals with their own ground: heather in clumps, shingle on a moor's banks, oasis reeds beside water;
//  - crystal kept to a share of the Underdark's groves;
//  - terraces and cliffs (docs/66 RM15) drawn into the ground in the kit's own colours (docs/164 ET7).
// Nothing here adds a rule: every choice only decides how a square the forge made looks.
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';
import type { Piece, Standing } from '../types';
import type { Hooks } from './hooks';
import { Canvas, type RGBA } from './canvas';
import { fromOklab, oklab } from './colour';
import { C, type Ctx } from './context';
import { groundOf, isLiquid, paintBanks, type Ground } from './ground';
import { drawLedges } from './country-ledges';

/** The countries this family draws (docs/164: a kit with no built walls). */
const COUNTRIES = new Set(['forest', 'autumn', 'winterwood', 'pinewood', 'mirewood', 'dunes', 'highland', 'jungle', 'underdark', 'ashlands']);
const mine = (ctx: Ctx): boolean => !ctx.built && COUNTRIES.has(ctx.set.kit);

/** Mud for part of the rough: one 2×2 block in `every` (the mire's board: one in three; the jungle's mud is loud, docs/168 open calls). */
const MUD_EVERY: Record<string, number> = { mirewood: 3, jungle: 4 };
/** The share of rough in a named rough's patches: the dunes' forge boards lay their rough as drift (`dunes_board.py.txt`), the woods' brambles are a patch among the brush. */
const NAMED_ROUGH_SHARE: Record<string, number> = { dunes: 0.6 };
/** Climates where water never freezes (the jungle's winter is its dry season; no ice in the Underdark or Ashlands). */
const NO_FROST = new Set(['jungle', 'desert', 'underdark', 'ashlands']);
/** The shared decal scatter's rate for the kits that use the default (index.ts `DECAL_RATE`), to keep to squares it left bare. */
const SCATTER_RATE: Record<string, number> = { underdark: 3, ashlands: 3, jungle: 4 };
const scatterRate = (ctx: Ctx): number => SCATTER_RATE[ctx.set.kit] ?? 6;

const isIce = (g: Ground | undefined): boolean => g?.piece.piece === 'ice';
const frozen = (ctx: Ctx): boolean => ctx.has('ice') && !NO_FROST.has(ctx.set.biome) && (ctx.set.kit === 'winterwood' || ctx.set.season === 'winter');

const ORTHO: ReadonlyArray<readonly [number, number]> = [[0, -1], [0, 1], [-1, 0], [1, 0]];
const SIDES: Array<['n' | 's' | 'w' | 'e', number, number]> = [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]];

function ground(ctx: Ctx, x: number, y: number, s: string): Ground | undefined {
  if (!mine(ctx)) return undefined;
  const t = ctx.sq.tile(x, y);
  if (!t) return undefined;
  // a square the rules made ice (docs/55): the kit's ice, frayed and lined below (named apart: it is not open water)
  if (t.ice && s !== 'water' && s !== 'deep' && ctx.has('ice')) return groundOf(ctx, 'ice', 'ice', x, y);
  if (s === 'rough') {
    const every = MUD_EVERY[ctx.set.kit];
    // ranked as rough (the boards rank mud 3 with it): the two fray into one another as a ground's variants do
    if (every && ctx.has('mud') && ctx.roll('mud', x >> 1, y >> 1) % every === 0) return groundOf(ctx, 'rough', 'mud', x, y);
    const named = ctx.named('rough_');
    if (named.length && ctx.patch(x, y, 3, NAMED_ROUGH_SHARE[ctx.set.kit] ?? 0.4, 'named rough') && ctx.roll('named rough hole', x, y) % 5) {
      return groundOf(ctx, 'rough', named[ctx.roll('named rough kind', Math.floor(x / 3), Math.floor(y / 3)) % named.length]!, x, y);
    }
    return undefined;
  }
  if (s === 'water') {
    // frozen water: sheets of ice over the open water, by seeded patch (the boreal glade, the frozen meres)
    if (frozen(ctx) && ctx.patch(x, y, 3, 0.55, 'ice sheet')) return groundOf(ctx, 'water', 'ice', x, y);
    // the Ashlands: the open flow is the hot heart of a lake or river, never at its bank
    if (ctx.set.kit === 'ashlands' && ctx.has('lava') && ORTHO.every(([dx, dy]) => { const n = ctx.sq.at(x + dx, y + dy); return n === undefined || n === 'water' || n === 'deep'; }) && ctx.patch(x, y, 3, 0.6, 'lava')) {
      return groundOf(ctx, 'lava', 'lava', x, y);
    }
  }
  return undefined;
}

/** Fray `hi`'s tile over the side of (x, y) that faces it, as paintGrounds does between grounds. */
function frayInto(ctx: Ctx, out: Canvas, x: number, y: number, s: 'n' | 's' | 'w' | 'e', hi: Ground): void {
  const prof = EDGE_DEPTHS[ctx.roll(`edge:${s}`, x, y) % EDGE_DEPTHS.length]!;
  for (let i = 0; i < C; i++) for (let k = 0; k < prof[i]!; k++) {
    const [ix, iy] = s === 'n' ? [i, k] : s === 's' ? [i, C - 1 - k] : s === 'w' ? [k, i] : [C - 1 - k, i];
    out.copy(x * C + ix, y * C + iy, hi.piece, ix, iy, hi.turn);
  }
}

/** Liquids by rank where the shared ranks don't part them: ice over open water and lava, water over lava (the Ashlands' board). */
const liquidRank = (g: Ground): number => (isIce(g) ? 3 : g.name === 'water' ? 2 : g.name === 'lava' ? 1 : 0);

function banks(ctx: Ctx, out: Canvas, grounds: ReadonlyArray<Ground | undefined>): boolean {
  if (!mine(ctx) || !grounds.some((g) => isIce(g) || g?.name === 'lava')) return false;
  const { w, h } = ctx.sq;
  // 1. ice over the water beside it, water over lava (the shared fray leaves equal ranks and an unturned ground's variants alone)
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const g = grounds[y * w + x];
    if (!g || !(isLiquid(g.name) || isIce(g))) continue;
    for (const [s, dx, dy] of SIDES) {
      const n = grounds[(y + dy) * w + x + dx];
      if (!n || !ctx.sq.on(x + dx, y + dy) || !(isLiquid(n.name) || isIce(n)) || isIce(g)) continue;
      if (!(isIce(n) || (n.name === 'water' && g.name === 'lava'))) continue;
      if (liquidRank(n) > liquidRank(g)) frayInto(ctx, out, x, y, s, n);
    }
  }
  // 2. the shared banks on the open liquids, the ice standing for neither land nor water
  const open = grounds.map((g) => (isIce(g) ? undefined : g));
  paintBanks(ctx, out, open, () => false);
  // 3. the ice's own bank: the land reaching in over it, then a dark wet line, no lip and no foam
  const wet = shoreWet(ctx);
  const CORNERS: Array<[string, number, number]> = [['ne', 1, -1], ['se', 1, 1], ['sw', -1, 1], ['nw', -1, -1]];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!isIce(grounds[y * w + x])) continue;
    const land: Array<{ s: string; n: Ground }> = [];
    for (const [s, dx, dy] of [...SIDES, ...CORNERS] as Array<[string, number, number]>) {
      if (!ctx.sq.on(x + dx, y + dy)) continue;
      const n = grounds[(y + dy) * w + x + dx];
      if (n && !isLiquid(n.name) && !isIce(n)) land.push({ s, n });
    }
    if (!land.length) continue;
    const prof: Record<string, readonly number[]> = {};
    for (const l of land) if (l.s.length === 1) prof[l.s] = EDGE_DEPTHS[ctx.roll(`bank:${l.s}`, x, y) % EDGE_DEPTHS.length]!;
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      let best = Infinity, from: Ground | undefined;
      for (const l of land) {
        let kk: number, dd: number;
        if (l.s.length === 1) {
          const [i, k] = l.s === 'n' ? [ix, iy] : l.s === 's' ? [ix, C - 1 - iy] : l.s === 'w' ? [iy, ix] : [iy, C - 1 - ix];
          kk = k; dd = prof[l.s]![i]!;
        } else { kk = Math.max(l.s.includes('w') ? ix : C - 1 - ix, l.s.includes('n') ? iy : C - 1 - iy); dd = 3; }
        if (kk - dd < best) { best = kk - dd; from = l.n; }
      }
      if (best < 0 && from) out.copy(x * C + ix, y * C + iy, from.piece, ix, iy, from.turn);
      else if (best === 0) out.set(x * C + ix, y * C + iy, wet);
    }
  }
  return true;
}

/** The kit's wet line (`_shore_seasons` before `_shore`), darkened a step for ice's edge. */
function shoreWet(ctx: Ctx): RGBA {
  const p = ctx.set.prompts;
  const seasons = p['_shore_seasons'] as Record<string, string[]> | undefined;
  const s = (ctx.set.season && seasons?.[ctx.set.season]) || (p['_shore'] as string[] | undefined);
  const c = s?.[1] ? s[1] : '#2a3440';
  const v = c.replace('#', '');
  const [L, a, b] = oklab([parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16), 255]);
  return fromOklab(L * 0.9, a, b);
}

/** Lay a flat piece on a square as the painter does (bottom-centred). */
function lay(ctx: Ctx, out: Canvas, piece: string, purpose: string, x: number, y: number): void {
  const p = ctx.pick(piece, purpose, x, y);
  if (p) out.draw(p, x * C + C / 2 - p.w / 2, y * C + C - p.h);
}

function afterFlat(ctx: Ctx, out: Canvas, grounds: ReadonlyArray<Ground | undefined>): void {
  if (!mine(ctx)) return;
  const { sq } = ctx, { w, h } = sq;
  const rate = scatterRate(ctx);
  // a square the shared scatter can't have used (it decorates plain floor and water only, at `rate`)
  const bare = (x: number, y: number): boolean => {
    const t = sq.tile(x, y), s = sq.at(x, y);
    if (!t || t.exit || t.hazard || t.objective || t.ice || t.cover > 0) return false;
    return !((s === 'floor' || s === 'water') && ctx.roll('decal', x, y) % 100 < rate);
  };
  const g = (x: number, y: number): Ground | undefined => (sq.on(x, y) ? grounds[y * w + x] : undefined);
  const wetBeside = (x: number, y: number): boolean => ORTHO.some(([dx, dy]) => { const s = sq.at(x + dx, y + dy); return s === 'water' || s === 'deep'; });
  const puddles = ['decal_puddle', 'decal_puddles'].filter((p) => ctx.has(p));
  const heather = ctx.has('decal_heather');
  const clump = new Set<number>();
  if (heather) {
    // heather a clump at a time in the clearings, most beside the heath or moss (pine_board.py.txt), never as confetti
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (sq.at(x, y) !== 'floor' || g(x, y)?.name !== 'floor') continue;
      const byPatch = ORTHO.some(([dx, dy]) => g(x + dx, y + dy)?.name === 'floor_patch');
      if (ctx.roll('heather', x, y) % 100 >= (byPatch ? 10 : 3)) continue;
      clump.add(y * w + x);
      for (const [dx, dy] of ORTHO) if (ctx.roll('heather clump', x * 4 + dx + 2, y * 4 + dy + 2) % 100 < 40) clump.add((y + dy) * w + x + dx);
    }
  }
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.at(x, y)!, gr = g(x, y);
    if (!gr || !bare(x, y)) continue;
    // puddles lie on mud (the mire's `decal_puddle`, `decal_puddles`; the jungle's rain)
    if (gr.piece.piece === 'mud' && puddles.length && ctx.roll('mud puddle', x, y) % 100 < 7) { lay(ctx, out, puddles[ctx.roll('mud puddle kind', x, y) % puddles.length]!, 'mud puddle pick', x, y); continue; }
    // the mire's lily pads: a fifth of its open water (mire_board.py.txt: 22 in 100), the scatter's share counted
    if (s === 'water' && ctx.set.kit === 'mirewood' && !isIce(gr) && ctx.has('decal_lily_pads') && ctx.roll('decal', x, y) % 100 < 22) { lay(ctx, out, 'decal_lily_pads', 'lily pick', x, y); continue; }
    if (s !== 'floor' || (gr.name !== 'floor' && gr.name !== 'floor_patch')) continue;
    if (heather && clump.has(y * w + x)) { lay(ctx, out, 'decal_heather', 'heather pick', x, y); continue; }
    // shingle on a moor's banks (highland notes: `decal_stones` on a bank)
    if (ctx.set.kit === 'highland' && ctx.has('decal_stones') && wetBeside(x, y) && ctx.roll('shingle', x, y) % 100 < 30) { lay(ctx, out, 'decal_stones', 'shingle pick', x, y); continue; }
    // reeds stand only beside the oasis's water (dunes_board.py.txt)
    if (ctx.has('decal_oasis_reeds') && wetBeside(x, y) && ctx.roll('oasis reeds', x, y) % 100 < 55) { lay(ctx, out, 'decal_oasis_reeds', 'reeds pick', x, y); continue; }
  }
  // terraces and cliffs in the kit's own colours (docs/164 ET7): the renderer leaves its vector ledges off
  drawLedges(ctx, out);
  ctx.ledges = true;
}

/** A big piece over a 2×2 block of wall squares: its block's top left, and the piece. */
interface Big { x: number; y: number; piece: Piece }
const bigs = new WeakMap<Ctx, Big[]>();

/** Big standing pieces (56×72, `big_oak`, `big_spruce`, `big_palms`, `big_tree`): seeded, spaced, at the wood's edge where the foot shows. */
function bigsOf(ctx: Ctx): Big[] {
  let list = bigs.get(ctx);
  if (list) return list;
  list = [];
  bigs.set(ctx, list);
  if (!mine(ctx)) return list;
  // big_pond stays unplaced: it is water drawn as a piece and would fight the banks (docs/166 C2)
  const names = ctx.named('big_').filter((n) => n !== 'big_pond');
  if (!names.length) return list;
  const { sq } = ctx, { w, h } = sq;
  const wall = (x: number, y: number): boolean => sq.isWall(x, y) && !sq.tile(x, y)?.exit;
  const open = (x: number, y: number): boolean => { const s = sq.at(x, y); return s !== undefined && s !== 'void' && !sq.isWall(x, y); };
  // one big piece to every 450 squares or so: a landmark, not a wood of them (the dunes' walled grove is one per board)
  const cap = Math.max(1, Math.floor((w * h) / 450));
  const found: Array<{ x: number; y: number; r: number }> = [];
  for (let y = 0; y + 1 < h; y++) for (let x = 0; x + 1 < w; x++) {
    if (!(wall(x, y) && wall(x + 1, y) && wall(x, y + 1) && wall(x + 1, y + 1))) continue;
    // its foot seen: open ground in front of the block's lower row
    if (!open(x, y + 2) && !open(x + 1, y + 2)) continue;
    const r = ctx.roll('big', x, y);
    if (r % 100 < 18) found.push({ x, y, r });
  }
  // the seeded order, not the board's, so the cap doesn't crowd them to the top
  found.sort((a, b) => a.r - b.r || a.y - b.y || a.x - b.x);
  for (const { x, y } of found) {
    if (list.length >= cap) break;
    if (list.some((b) => Math.abs(b.x - x) < 6 && Math.abs(b.y - y) < 5)) continue;
    const piece = ctx.pick(names[ctx.roll('big kind', x, y) % names.length]!, 'big pick', x, y);
    if (piece) list.push({ x, y, piece });
  }
  return list;
}

function claim(ctx: Ctx): Set<number> {
  const out = new Set<number>();
  const { w } = ctx.sq;
  for (const b of bigsOf(ctx)) for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]] as const) out.add((b.y + dy) * w + b.x + dx);
  return out;
}

function afterStanding(ctx: Ctx, out: Canvas, _grounds: ReadonlyArray<Ground | undefined>, standing: Standing[]): void {
  if (!mine(ctx)) return;
  // the big piece bottom-centred on its block, its foot on the block's lower row (pine_board.py.txt's `G`)
  for (const b of bigsOf(ctx)) {
    const p = b.piece, cx = (b.x + 1) * C, foot = (b.y + 2) * C;
    const px = cx - Math.floor(p.w / 2), py = foot - p.h;
    out.ellipse(cx - 18, foot - 6, cx + 18, foot, [10, 20, 10, 80]);
    standing.push({ kind: 'big', at: { x: b.x, y: b.y + 1 }, x: px, y: py, w: p.w, h: p.h, foot: (b.y + 1) * C + C / 2, frame: p.frame, ...(ctx.roll('big flip', b.x, b.y) % 2 ? { flipX: true } : {}) });
  }
  // the Underdark: crystal kept to a share of a grove, the loudest thing here (its notes); the rest of the grove is fungus
  if (ctx.set.kit === 'underdark' && ctx.has('grove_fungus_stand')) {
    for (const st of standing) {
      if (st.kind !== 'grove' || !st.frame?.startsWith('grove_crystal_grotto') || ctx.roll('crystal share', st.at.x, st.at.y) % 3 === 0) continue;
      const p = ctx.pick('grove_fungus_stand', 's', st.at.x, st.at.y)!;
      st.frame = p.frame; st.x = st.at.x * C + C / 2 - p.w / 2; st.y = (st.at.y + 1) * C - p.h; st.w = p.w; st.h = p.h;
    }
  }
}

export const hooks: Hooks = { ground, banks, afterFlat, claim, afterStanding };
